/**
 * CircuitBG.js (Replaces HexGrid.js)
 * Handles:
 *  1. Clock / frame counter / system metrics for the outer-nav monitor widget
 *  2. Global --hex-hue cycle that drives BOTH the card-glow border
 *     AND the circuit-bg background color in perfect sync
 *
 * Color Sync Explained:
 *   - --hex-hue is an integer (0–360) set on :root
 *   - .card-glow::before uses hsl(var(--hex-hue) 100% ...) directly
 *   - .circuit-bg::before uses filter: hue-rotate(calc((var(--hex-hue) - 210) * 1deg))
 *     because the SVG base stroke is #1a7ee0 (~hue 210), so offset keeps them in sync
 */
class CircuitBG {
    constructor(containerId) {
        this.container = document.getElementById(containerId);
        this.clockEl = document.getElementById('hex-clk');
        this.frameEl = document.getElementById('hex-ft');
        this.metrics = {
            cpu: { val: document.getElementById('hex-cv'), fill: document.getElementById('hex-cf') },
            mem: { val: document.getElementById('hex-mv2'), fill: document.getElementById('hex-mf2') },
            net: { val: document.getElementById('hex-nv'), fill: document.getElementById('hex-nf') }
        };

        this.active = false;
        this.frameCount = 0;
        this.timers = [];
        this.hueRaf = null;
        this.hueStart = null;

        // Hue cycle config — 20 seconds full loop for a slow, premium feel
        this.hueDuration = 20000;
        this.isMobile = window.innerWidth < 768;
        this.lastFrameUpdate = 0;
        this.lastHueUpdate = 0;
    }

    start() {
        if (this.active) return;
        this.active = true;
        this.frameCount = 0;

        this.updateClock();
        this.updateMetrics();
        this.animate();
        this.startHueCycle();
    }

    stop() {
        this.active = false;
        this.timers.forEach(t => clearTimeout(t));
        this.timers = [];
        if (this.hueRaf) cancelAnimationFrame(this.hueRaf);
        this.hueRaf = null;
    }

    /* ── Hue Cycle ─────────────────────────────────────────────── */
    startHueCycle() {
        this.hueStart = performance.now();
        this.tickHue();
    }

    tickHue(currentTime) {
        if (!this.active) return;

        // Throttle hue updates on mobile (30fps equivalent)
        const delta = currentTime - this.lastHueUpdate;
        if (this.isMobile && delta < 33) {
            this.hueRaf = requestAnimationFrame((t) => this.tickHue(t));
            return;
        }
        this.lastHueUpdate = currentTime;

        const elapsed = currentTime - this.hueStart;
        const t = (elapsed % this.hueDuration) / this.hueDuration; // 0→1
        const hue = Math.round(t * 360); // 0→360 degrees
        document.documentElement.style.setProperty('--hex-hue', hue);
        this.hueRaf = requestAnimationFrame((t) => this.tickHue(t));
    }

    /* ── Clock ─────────────────────────────────────────────────── */
    updateClock() {
        if (!this.active) return;
        const now = new Date();
        const h = String(now.getHours()).padStart(2, '0');
        const m = String(now.getMinutes()).padStart(2, '0');
        const s = String(now.getSeconds()).padStart(2, '0');
        if (this.clockEl) this.clockEl.textContent = `${h}:${m}:${s}`;
        this.timers.push(setTimeout(() => this.updateClock(), 1000));
    }

    /* ── Metrics ───────────────────────────────────────────────── */
    updateMetrics() {
        if (!this.active) return;
        Object.keys(this.metrics).forEach(key => {
            const m = this.metrics[key];
            const val = Math.floor(Math.random() * 40) + 30;
            if (m.val) m.val.textContent = `${val}%`;
            if (m.fill) m.fill.style.width = `${val}%`;
        });
        this.timers.push(setTimeout(() => this.updateMetrics(), 2000 + Math.random() * 1000));
    }

    /* ── Frame Counter ─────────────────────────────────────────── */
    animate(currentTime) {
        if (!this.active) return;

        // Throttle frame counter on mobile
        const delta = currentTime - this.lastFrameUpdate;
        if (this.isMobile && delta < 100) { // Update counter only ~10 times a sec on mobile
            requestAnimationFrame((t) => this.animate(t));
            return;
        }
        this.lastFrameUpdate = currentTime;

        this.frameCount++;
        if (this.frameEl) {
            this.frameEl.textContent = `FRAME::${String(this.frameCount).padStart(4, '0')}`;
        }
        requestAnimationFrame((t) => this.animate(t));
    }
}

// Expose globally
window.CircuitBG = new CircuitBG('hex-grid-container');
